import EmptyLink from '@aytm/app/swidget/base/components/EmptyLink'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'
import { getSmallAssetPath } from '@aytm/lib/common/assets'
import { EcommerceResponse, IEcommerceAnswerSchema, IEcommerceQuestionSchema } from '../../types.ts'
import { formatPriceWithLocale } from '../../utils/formatPriceWithLocale.ts'
import AddToCartButton from './AddToCartButton.tsx'
import style from './CartRail.module.scss'
import basketWhiteIcon from '../assets/icons/basketWhite.svg'
import basketGreyIcon from '../assets/icons/basketGrey.svg'

interface ICartRailProps {
  schema: IEcommerceQuestionSchema
  response: EcommerceResponse
  total: number
  totalPrice: number
  findability: boolean
  selectedId: string
  onCartOpen: () => void
  onItemSelect: (index: number) => void
  onItemClose: () => void
  onAddToCart: (index: number, qnt: number) => void
  onDeleteFromCart: (index: number) => void
}

const CartRail = ({
  schema,
  response,
  total,
  totalPrice,
  findability,
  selectedId,
  onCartOpen,
  onItemSelect,
  onItemClose,
  onAddToCart,
  onDeleteFromCart
}: ICartRailProps) => {
  const { t, languageTag } = useSurveyLocale()
  const price = (value: number) => formatPriceWithLocale(value, schema.price_mask, languageTag, true, false)

  const items = schema.answers
    .map(answer => ({ answer, line: response.answers[answer.index] }))
    .filter(({ answer, line }) => line.v > 0 && !answer.na)

  const onProductPageOpen = (answer: IEcommerceAnswerSchema) => {
    if (selectedId === answer.id) return
    onItemClose()
    onItemSelect(answer.index)
  }

  // Both basket buttons open the full basket; in findability the basket is inert, like every store's cart.
  const basketButton = (label: string, className: string, disabled = false) =>
    findability ? (
      <div className={style.stretch}>
        <EmptyLink color="#00539f">
          <span className={className}>{label}</span>
        </EmptyLink>
      </div>
    ) : (
      <button type="button" className={className} disabled={disabled} onClick={onCartOpen}>
        {label}
      </button>
    )

  return (
    <aside className={style.rail} data-testid="SW-EC-AddToCartSidebar">
      <div className={style.sticky}>
        <section className={style.card}>
          <div className={style.cardTitle}>{t('questions', 'ec', 'tesco', 'basket')}</div>
          <div className={style.summary}>
            <span className={style.basketBadge}>
              <img src={basketWhiteIcon} alt="" role="presentation" className={style.basketIcon} />
            </span>
            <span className={style.summaryText}>
              <span className={style.total} dangerouslySetInnerHTML={{ __html: price(totalPrice) }} />
              <span className={style.guidePrice}>{t('questions', 'ec', 'tesco', 'guidePrice')}</span>
            </span>
          </div>
          {basketButton(t('questions', 'ec', 'tesco', 'checkout'), style.checkout, 0 === total)}
        </section>

        <div className={style.items}>
          <section className={`${style.card} ${style.groceries}`}>
            <div className={`${style.cardTitle} ${style.cardTitleSmall}`}>
              {t('questions', 'ec', 'tesco', 'groceries')}
            </div>
            {total > 0 && (
              <>
                <div className={style.guideRow}>
                  <span>{t('questions', 'ec', 'tesco', 'guidePrice')}</span>
                  <span dangerouslySetInnerHTML={{ __html: price(totalPrice) }} />
                </div>
                <EmptyLink color="#00539f">
                  <span className={style.guideLink}>{t('questions', 'ec', 'tesco', 'whatsGuidePrice')}</span>
                </EmptyLink>
              </>
            )}
            <div className={style.slotText}>{t('questions', 'ec', 'tesco', 'reserveSlot')}</div>
            {total > 0 && (
              <div className={style.stretch}>
                <EmptyLink color="#00539f">
                  <span className={`${style.outlineButton} ${style.bookSlot}`}>
                    {t('questions', 'ec', 'tesco', 'bookSlot')}
                  </span>
                </EmptyLink>
              </div>
            )}
          </section>
          {0 === total ? (
            <div className={style.empty}>
              <span className={style.emptyBadge}>
                <img src={basketGreyIcon} alt="" role="presentation" className={style.basketIcon} />
              </span>
              <div className={style.emptyTitle}>{t('questions', 'ec', 'tesco', 'basketEmpty')}</div>
              <div className={style.emptyHint}>{t('questions', 'ec', 'tesco', 'basketEmptyHint')}</div>
            </div>
          ) : (
            items.map(({ answer, line }) => (
              <div className={style.item} key={answer.id}>
                <div className={style.itemThumb} onClick={() => onProductPageOpen(answer)}>
                  {answer.image ? (
                    <span
                      className={style.itemImage}
                      role="img"
                      aria-label={answer.image.alt_text || t('questions', 'img', 'noAlt')}
                      style={{ backgroundImage: `url('${getSmallAssetPath(answer.image)}')` }}
                    />
                  ) : (
                    <span className={style.itemNoImage} />
                  )}
                </div>
                <div className={style.itemInfo}>
                  <div
                    className={style.itemTitle}
                    onClick={() => onProductPageOpen(answer)}
                    dangerouslySetInnerHTML={{ __html: answer.text }}
                  />
                  <div className={style.itemPrice} dangerouslySetInnerHTML={{ __html: price(line.v * line.price) }} />
                  <div className={style.stepper}>
                    <AddToCartButton
                      amount={line.v}
                      limit={schema.max_qty_per_product}
                      appearance="small"
                      onAdd={qnt => onAddToCart(answer.index, qnt)}
                    />
                  </div>
                </div>
                <button
                  type="button"
                  className={style.remove}
                  onClick={() => onDeleteFromCart(answer.index)}
                  aria-label={t('questions', 'ec', 'tesco', 'remove')}
                >
                  <i className="pi pi-times" />
                </button>
              </div>
            ))
          )}
        </div>

        {total > 0 && (
          <div className={style.viewBasket}>
            {basketButton(t('questions', 'ec', 'tesco', 'viewFullBasket'), style.outlineButton)}
          </div>
        )}
      </div>
    </aside>
  )
}

export default CartRail
